import React from 'react';
import { Box, Typography, Grid, Card } from '@mui/material';
import ComparisonTable from '../../../comparisonPond/components/ComparisonTable';
import PondMultiSelect from 'modules/comparisonPond/components/CycleMultiSelect';
import ComparisonChart from 'modules/comparisonPond/components/ComparisonChart';
import { useTranslation } from 'react-i18next';
import { useGetDOComparison, useGetFoodComparison, useGetFoodStandard, useGetGrowthComparison, useGetGrowthStandard, useGetPHComparison } from 'modules/comparisonPond/api/queries';
import { useSearchParams } from 'react-router-dom';

import Page from 'components/Page';

const ComparisonPage: React.FC = () => {
  const { t } = useTranslation('pondComparison');
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get('cycleIds');
  const urlCycleIds = idParam ? idParam.split(',').filter(Boolean) : [];

  const { data: comparisonFoodData, isLoading: isLoadingComparisonFood } = useGetFoodComparison(urlCycleIds);
  const { data: comparisonFoodStandardData, isLoading: isLoadingComparisonFoodStandard } = useGetFoodStandard(urlCycleIds);
  const { data: comparisonGrowthData, isLoading: isLoadingComparisonGrowth } = useGetGrowthComparison(urlCycleIds);
  const { data: comparisonGrowthStandardData, isLoading: isLoadingComparisonGrowthStandard } = useGetGrowthStandard(urlCycleIds);
  const { data: comparisonDOData, isLoading: isLoadingComparisonDO } = useGetDOComparison(urlCycleIds);
  const { data: comparisonPHData, isLoading: isLoadingComparisonPH } = useGetPHComparison(urlCycleIds);

  const hasSelection = Array.isArray(urlCycleIds) && urlCycleIds.length > 0;

  return (
    <Page title={t('comparison')}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' } }}
        >
          {t('comparison')}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <PondMultiSelect />
          </Grid>

          {/* Right: Charts */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ComparisonChart
                  title={t('feed_conversion_ratio')}
                  cycleData={comparisonFoodData ?? undefined}
                  // standardData={comparisonFoodStandardData}
                  isLoading={hasSelection ? (isLoadingComparisonFood || isLoadingComparisonFoodStandard) : false}
                  yUnit={t('unit.kg')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ComparisonChart
                  title={t('growth_rate')}
                  cycleData={comparisonGrowthData ?? undefined}
                  // standardData={comparisonGrowthStandardData}
                  isLoading={hasSelection ? (isLoadingComparisonGrowth || isLoadingComparisonGrowthStandard) : false}
                  yUnit={t('unit.one_per_kg')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ComparisonChart
                  title={t('do_value')}
                  cycleData={comparisonDOData ?? undefined}
                  isLoading={hasSelection ? isLoadingComparisonDO : false}
                  yUnit={'ppm'}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ComparisonChart
                  title={t('ph_value')}
                  cycleData={comparisonPHData ?? undefined}
                  isLoading={hasSelection ? isLoadingComparisonPH : false}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <Card sx={{ p: 2, overflow: 'auto' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                {t('comparison_table')}
              </Typography>
              <ComparisonTable />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

export default ComparisonPage;
